import { useCallback, useEffect, useRef, useState } from 'react';

import type { CapabilityMap, RoleResponse } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

export interface CreateRoleBody {
  name: string;
  slug: string;
  description?: string | undefined;
  capabilities: CapabilityMap;
}

export interface UpdateRoleBody {
  name?: string | undefined;
  description?: string | undefined;
  capabilities?: CapabilityMap | undefined;
}

/**
 * ViewModel for tenant roles (capability RBAC). Mirrors use-users.ts. Any
 * authenticated user may LIST roles (to populate the assignment dropdown);
 * mutations are gated server-side on the manage_roles capability.
 */
export function useRoles({ enabled = true }: { enabled?: boolean } = {}) {
  const apiClient = useApiClient();
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const loadedRef = useRef(false);

  const fetch = useCallback(() => {
    apiClient
      .listRoles()
      .then((res) => {
        setRoles(res.data);
        loadedRef.current = true;
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load roles');
        if (!loadedRef.current) setStatus('error');
      });
  }, [apiClient]);

  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    fetchRef.current();
  }, [enabled]);

  const createRole = useCallback(
    async (body: CreateRoleBody) => {
      const created = await apiClient.createRole(body);
      setRoles((prev) => [...prev, created]);
      return created;
    },
    [apiClient],
  );

  const updateRole = useCallback(
    async (id: string, body: UpdateRoleBody) => {
      const updated = await apiClient.updateRole(id, body);
      setRoles((prev) => prev.map((r) => (r.id === id ? updated : r)));
      return updated;
    },
    [apiClient],
  );

  const deleteRole = useCallback(
    async (id: string) => {
      await apiClient.deleteRole(id);
      setRoles((prev) => prev.filter((r) => r.id !== id));
    },
    [apiClient],
  );

  const reload = useCallback(() => fetchRef.current(), []);

  return { roles, status, error, createRole, updateRole, deleteRole, reload };
}
